import {
  DSButton,
  DSInput,
  DSModal,
  DSTextArea,
} from '@hundred5/design-system';
import React, { useState } from 'react';

import {
  useCreateInterviewMutation,
  useGenerateInterviewAiMutation,
  useUpdateInterviewMutation,
} from '@/queries/interviews/mutations';
import { Interview } from '@/queries/types';
import { useHistory } from 'react-router';
import { Loader } from './ui/loader';

interface InterviewAiCreatorProps {
  workspaceId: number;
  jobOpeningId: number;
  stageId: number;
  onClose: () => void;
  interview?: Interview;
}

export function InterviewAICreator({
  workspaceId,
  jobOpeningId,
  stageId,
  onClose,
  interview,
}: InterviewAiCreatorProps) {
  const history = useHistory();

  const [durationMin, setDurationMin] = useState(60);
  const [userContext, setUserContext] = useState('');

  const createInterviewMutation = useCreateInterviewMutation({
    meta: {
      invalidates: 'all',
    },
    onSuccess: (data) => {
      history.push(
        `/admin/${workspaceId}/openings/${jobOpeningId}/${stageId}/interview/${data.id}/settings`
      );
    },
  });
  const updateInterviewMutation = useUpdateInterviewMutation({
    onSuccess: () => {
      history.push(
        `/admin/${workspaceId}/openings/${jobOpeningId}/${stageId}/interview/${interview?.id}/settings`
      );
    },
    meta: { invalidates: 'all' },
  });
  const generateInterviewAiMutation = useGenerateInterviewAiMutation({
    onSuccess: (data) => {
      if (!interview) {
        return;
      }

      updateInterviewMutation.mutate({
        workspaceId: workspaceId,
        openingId: jobOpeningId,
        stageId,
        data: {
          ...interview,
          kit: {
            isScorecard: interview?.kit?.isScorecard ?? false,
            sections: data,
          },
        },
      });
    },
  });

  function handleGenerate() {
    const generateParams = {
      durationMin,
      userContext,
    };

    if (interview) {
      generateInterviewAiMutation.mutate({
        workspaceId: workspaceId,
        openingId: jobOpeningId,
        stageId,
        data: generateParams,
      });
    } else {
      createInterviewMutation.mutate({
        workspaceId: workspaceId,
        openingId: jobOpeningId,
        stageId: stageId,
        data: {
          name: 'Interview',
          generateSections: true,
          generateParams,
        },
      });
    }
  }

  return (
    <DSModal
      open
      contentStyle={{ padding: 0, width: '100%' }}
      onClose={onClose}
      shouldCloseOnEsc={
        !createInterviewMutation.isPending &&
        !generateInterviewAiMutation.isPending
      }
      shouldCloseOnOverlayClick={
        !createInterviewMutation.isPending &&
        !generateInterviewAiMutation.isPending
      }
    >
      {!createInterviewMutation.isPending &&
      !generateInterviewAiMutation.isPending ? (
        <DSModal.CloseButton onClick={onClose} />
      ) : null}
      <header className="pb-4 p-9 border-b-1 border-solid border-secondary">
        <h1 className="text-h4">Generate your interview questions using AI</h1>
      </header>

      {createInterviewMutation.isPending ||
      generateInterviewAiMutation.isPending ? (
        <Loader />
      ) : (
        <>
          <div className="rounded-lg p-6 my-7 mx-9 bg-secondary">
            We will generate a{' '}
            <DSInput
              className="inline-block !w-20 relative"
              type="number"
              value={durationMin}
              onChange={(event) => setDurationMin(Number(event.target.value))}
            />{' '}
            min structured interview based on your job description. You can also
            provide any other context below.
            <DSTextArea
              value={userContext}
              onChange={(event) => setUserContext(event.target.value)}
              placeholder="Interview stage, company culture, specific skills to assess, interview style preferences, etc."
              className="mt-6 min-h-40"
            />
          </div>

          <footer className="flex justify-end gap-2 pt-4 p-9">
            <DSButton variant="secondary" onClick={onClose}>
              Cancel
            </DSButton>
            <DSButton
              type="submit"
              variant="primary-purple"
              onClick={handleGenerate}
            >
              Generate
            </DSButton>
          </footer>
        </>
      )}
    </DSModal>
  );
}
